/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Divider, Flex, Group, Stack, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { IconCheck, IconX } from '@tabler/icons';
import { FC, ReactNode, useContext } from 'react';
import { replaceTemplatePlaceholdersWithValues } from '../../../Service/Placeholder/PlaceholderResover';
import { translate } from '../../../Service/PlaceholderTranslationService';
import { FormatS } from '../../../Service/FormatS';
import { TaxType } from '../../../Templates/Documents/DocumentS';
import ResourcePositionAssignment from '../../../Organismns/Settings/Documents/Positions/ResourcePositionAssignment';
import { Language, PositionDto } from '../../../Service/DocumentT';
import BookitupDocument, { Currency } from '../../../Types/BookitupDocument';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import { useEventData } from '../../../Service/Context/EventParticipationContextV2';
import DocumentLanguageS from '../../../Service/DocumentLanguageS';
import { LicenceS } from '../../../Service/LicenceS';
import { useJwtClaims } from '../../../Service/Context/AuthContext';

export const PositionPrev: FC<{
  position: PositionDto;
  favoritePosition: boolean;
  document?: BookitupDocument;
  taxType?: TaxType;
  language?: Language | null;
}> = ({ ...rest }) => {
  const { isEventScope } = useEventScope();

  if (isEventScope) {
    return <DocumentPositionPreview {...rest} />;
  }

  return <ProductPositionPreview {...rest} />;
};

export const ProductPositionPreview: FC<{
  position: PositionDto;
  favoritePosition: boolean;
  document?: BookitupDocument;
  currency?: Currency;
  taxType?: TaxType;
  language?: Language | null;
}> = ({ position, favoritePosition, document, taxType = 'gross', currency = 'EUR', language }) => {
  const { i18n } = useLingui();
  const { label, subtext, amount: quantity } = position;
  const integerQuantity = parseInt(quantity, 10);
  const isSummable = !Number.isNaN(integerQuantity);

  const translatedLabel =
    !label || label === '' ? 'Unbenannt' : translate.toLocale(label, false, false, true, i18n, 'de', () => null, {});

  const translatedDescription =
    !subtext || subtext === ''
      ? 'Noch nichts hinzugefügt ...'
      : translate.toLocale(subtext, false, false, true, i18n, 'de', () => null, {});

  const translatedQuantity = isSummable
    ? integerQuantity
    : translate.toLocale(quantity, false, false, true, i18n, 'de', () => null, {});

  return (
    <PositionPreview
      summable={isSummable}
      title={translatedLabel}
      description={translatedDescription}
      position={position}
      quantity={translatedQuantity}
      favoritePosition={favoritePosition}
      currency={currency}
      taxType={taxType}
      language={language}
      showOfferSettings={document?.documentType === 'OFFER'}
    />
  );
};

export const DocumentPositionPreview: FC<{
  position: PositionDto;
  taxType?: TaxType;
  document?: any;
  language?: Language | null;
}> = ({ position, taxType, document, language }) => {
  const { i18n } = useLingui();
  const { event, location, customer, customAttributeValues } = useEventData();
  // TODO: Rework with typescript already
  const { label, subtext, amount } = position;

  const integerQuantity = parseInt(amount, 10);
  const isSummable = !Number.isNaN(integerQuantity);

  const translatedQuantity = isSummable
    ? integerQuantity
    : translate.toLocale(amount, false, false, false, i18n, 'de', () => null, {
        event,
        location,
        customer,
        document,
        customAttributeValues,
      });

  const placeholderLabel = label
    ? replaceTemplatePlaceholdersWithValues(i18n, label, {
        event,
        _document: document,
        eventLocation: location,
        customer,
      })
    : 'Unbenannt';
  const placeholderSubtext = subtext
    ? replaceTemplatePlaceholdersWithValues(i18n, subtext, {
        event,
        _document: document,
        eventLocation: location,
        customer,
      })
    : 'Noch nichts hinzugefügt ...';

  return (
    <PositionPreview
      summable
      taxType={taxType}
      title={translate.toLocale(placeholderLabel, false, false, true, i18n, 'de', () => null, {})}
      description={translate.toLocale(placeholderSubtext, false, false, true, i18n, 'de', () => null, {})}
      quantity={translatedQuantity}
      position={position}
      favoritePosition={false}
      currency={document.currency}
      language={language}
      showOfferSettings={document.documentType === 'OFFER'}
    />
  );
};

interface PositionPreviewProps {
  title: ReactNode;
  description: ReactNode;
  quantity: ReactNode;
  position: PositionDto;
  summable?: boolean;
  taxType?: TaxType;
  favoritePosition: boolean;
  currency?: Currency;
  language?: Language | null;
  showOfferSettings?: boolean;
}

const PositionPreview: FC<PositionPreviewProps> = ({
  title,
  description,
  quantity,
  position,
  summable,
  taxType,
  favoritePosition,
  currency = 'EUR',
  language = 'german',
  showOfferSettings = false,
}) => {
  const { priceUnit, netPriceUnit, discount, price, tax, unit, netPrice, positionType, optional, negotiable } =
    position;
  const { value: discountValue, type: discountType } = discount;
  const { isActive } = useContext(ResourceAddonContext);
  const t = (id: string) => DocumentLanguageS.translate(language, id);

  return (
    <Stack sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}>
      <Text weight="bolder" size="xs" sx={{ maxWidth: '95%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {title}
      </Text>
      <Text size="xs" sx={{ maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {description}
      </Text>
      {positionType !== 'FREE_TEXT' && (
        <>
          <Flex direction="row" wrap="wrap" gap="md" rowGap="xs">
            <Text size="xs" weight="bolder">
              {`${t('document.position.quantity')}/${t('document.position.unit')}:`}{' '}
              <Text span weight="normal" className="placeholder-xs">
                {quantity}
                {` ${summable && unit ? unit : ''}`}
              </Text>
            </Text>
            <Text size="xs" weight="bolder">
              {`${t('document.position.tax')}:`}
              <Text span weight="normal">
                {` ${tax || '____'} %`}
              </Text>
            </Text>
            <Text size="xs" weight="bolder">
              {`${t('document.position.unit_price')}:`}
              <Text span weight="normal">
                {` ${FormatS.formatPriceWithCurrency(currency, taxType === 'net' ? netPriceUnit : priceUnit)}`}
              </Text>
            </Text>
            {discountValue > 0 && (
              <Text size="xs" weight="bolder">
                {`${t('document.position.discount')}:`}
                <Text span weight="normal">
                  {` ${discountValue} ${discountType === 'ABSOLUTE' ? '€' : '%'}`}
                </Text>
              </Text>
            )}
          </Flex>
          <Divider />
          <Group position="apart">
            <Text size="xs" weight="bolder">
              {`${t('document.position.total')}:`}
            </Text>
            <Text size="xs" weight="bolder">
              {FormatS.formatPriceWithCurrency(currency, taxType === 'gross' ? price : netPrice)}
            </Text>
          </Group>

          {isActive && <ResourcePositionAssignment preview position={position} favoritePosition={favoritePosition} />}
        </>
      )}
    </Stack>
  );
};
